/**
 * Shop Integrations Service
 * Multi-tenant mağaza entegrasyonları için frontend servis.
 *
 * Desteklenen platformlar (hepsi API anahtarı / client-credentials, OAuth-redirect yok):
 * Shopify, JTL, WooCommerce, Shopware 6, Magento 2, BigCommerce, PrestaShop, İkas.
 * Alan tanımları backend services/shopAdapters ile birebir aynıdır.
 */

import api from '@/lib/api';
import { t } from '@/i18n/t';

export type PlatformType = 'shopify' | 'jtl' | 'woocommerce' | 'shopware' | 'magento' | 'bigcommerce' | 'prestashop' | 'ikas' | 'opencart';

export interface PlatformFieldDef {
  name: string;
  label: string;
  type: 'text' | 'password';
  required: boolean;
  placeholder?: string;
  help?: string;
  default?: string;
}

interface PlatformDef {
  name: string;
  icon: string;
  authHint: string;
  fields: PlatformFieldDef[];
}

export const PLATFORMS: Record<Exclude<PlatformType, 'opencart'>, PlatformDef> = {
  shopify: {
    name: 'Shopify',
    icon: '🛍️',
    authHint: 'Shopify Admin → Settings → Apps and sales channels → Develop apps → Create app → Admin API access token (read_orders, read_customers).',
    fields: [
      { name: 'shop_url', label: 'Shop URL (.myshopify.com)', type: 'text', required: true, placeholder: 'my-store.myshopify.com' },
      { name: 'access_token', label: 'Admin API Access Token', type: 'password', required: true, placeholder: 'shpat_…' },
      { name: 'api_version', label: 'API Version', type: 'text', required: false, placeholder: '2024-10', default: '2024-10' },
    ],
  },
  jtl: {
    name: 'JTL Shop',
    icon: '📦',
    authHint: 'API key of your JTL gateway (VynDesk JTL connector).',
    fields: [
      { name: 'jtl_url', label: 'JTL Gateway URL', type: 'text', required: true, placeholder: 'https://jtl-gateway.example.com' },
      { name: 'api_key', label: 'API Key', type: 'password', required: true },
      { name: 'client_id', label: 'Client ID', type: 'text', required: false },
    ],
  },
  woocommerce: {
    name: 'WooCommerce',
    icon: '🛒',
    authHint: 'WooCommerce → Settings → Advanced → REST API → Add key (Read permission is enough).',
    fields: [
      { name: 'site_url', label: 'Store URL', type: 'text', required: true, placeholder: 'https://shop.example.com' },
      { name: 'consumer_key', label: 'Consumer Key', type: 'text', required: true, placeholder: 'ck_…' },
      { name: 'consumer_secret', label: 'Consumer Secret', type: 'password', required: true, placeholder: 'cs_…' },
    ],
  },
  shopware: {
    name: 'Shopware 6',
    icon: '🧩',
    authHint: 'Shopware Admin → Settings → System → Integrations → create integration (read access to orders).',
    fields: [
      { name: 'shop_url', label: 'Shop URL', type: 'text', required: true, placeholder: 'https://shop.example.com' },
      { name: 'access_key_id', label: 'Access Key ID', type: 'text', required: true, placeholder: 'SWIA…' },
      { name: 'secret_access_key', label: 'Secret Access Key', type: 'password', required: true },
    ],
  },
  magento: {
    name: 'Magento 2',
    icon: '🔧',
    authHint: 'Magento Admin → System → Integrations → Add New Integration → Activate → copy the Access Token.',
    fields: [
      { name: 'base_url', label: 'Store URL', type: 'text', required: true, placeholder: 'https://shop.example.com' },
      { name: 'access_token', label: 'Access Token', type: 'password', required: true },
      { name: 'admin_path', label: 'Admin path', type: 'text', required: false, placeholder: 'admin', help: 'Only for "open in Magento" links (default: admin).' },
    ],
  },
  bigcommerce: {
    name: 'BigCommerce',
    icon: '🏬',
    authHint: 'BigCommerce → Settings → API → Store-level API accounts → Create (Orders: read-only, Information & Settings: read-only).',
    fields: [
      { name: 'store_hash', label: 'Store Hash', type: 'text', required: true, placeholder: 'abc123xyz' },
      { name: 'access_token', label: 'Access Token', type: 'password', required: true },
    ],
  },
  prestashop: {
    name: 'PrestaShop',
    icon: '🏪',
    authHint: 'PrestaShop → Advanced Parameters → Webservice → enable → Add key (GET on customers, orders, order_states, addresses, countries, currencies, carriers).',
    fields: [
      { name: 'shop_url', label: 'Shop URL', type: 'text', required: true, placeholder: 'https://shop.example.com' },
      { name: 'api_key', label: 'Webservice Key', type: 'password', required: true },
      { name: 'admin_url', label: 'Admin URL', type: 'text', required: false, placeholder: 'https://shop.example.com/admin123abc', help: 'Only for "open in PrestaShop" links.' },
    ],
  },
  ikas: {
    name: 'İkas',
    icon: '🇹🇷',
    authHint: 'ikas Admin → Settings → Apps → Create private app → copy Client ID and Client Secret.',
    fields: [
      { name: 'store_name', label: 'Store name (<store>.myikas.com)', type: 'text', required: true, placeholder: 'mystore' },
      { name: 'client_id', label: 'Client ID', type: 'text', required: true },
      { name: 'client_secret', label: 'Client Secret', type: 'password', required: true },
    ],
  },
};

export const PLATFORM_ORDER: PlatformType[] = ['shopify', 'jtl', 'woocommerce', 'shopware', 'magento', 'bigcommerce', 'prestashop', 'ikas'];

export interface ShopIntegration {
  id: string;
  organization_id: string;
  platform_type: PlatformType;
  platform_name: string;
  description?: string;
  connection_config: Record<string, any>;
  is_active: boolean;
  is_default: boolean;
  last_test_at?: string;
  last_test_status?: 'success' | 'failed' | 'pending' | 'timeout' | 'unauthorized';
  last_test_error?: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  last_request_at?: string;
  created_at: string;
  updated_at: string;
  created_by_id?: string;
  created_by_name?: string;
}

export interface CreateShopIntegrationData {
  platformType: PlatformType;
  platformName: string;
  description?: string;
  connectionConfig: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface UpdateShopIntegrationData {
  platformName?: string;
  description?: string;
  connectionConfig?: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface ShopIntegrationsResponse {
  success: boolean;
  message: string;
  data: {
    integrations: ShopIntegration[];
    organizationId: string;
    meta: {
      total: number;
      active: number;
      platforms: string[];
    };
  };
}

export interface ShopIntegrationResponse {
  success: boolean;
  message: string;
  data: {
    integration: ShopIntegration;
  };
}

export interface TestResult {
  success: boolean;
  message: string;
  error?: string;
  responseTime?: number;
  statusCode?: number;
  shopName?: string;
  shopDomain?: string;
}

export interface TestResponse {
  success: boolean;
  message: string;
  data: {
    testResult: TestResult;
    integrationId: string;
    testedAt: string;
  };
}

export interface PlatformConfigField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'number';
  required: boolean;
  default?: string;
  placeholder?: string;
  description?: string;
}

export interface SupportedPlatform {
  type: PlatformType;
  name: string;
  description: string;
  configFields: PlatformConfigField[];
  testable: boolean;
  authHint?: string | null;
  icon?: string;
}

export interface PlatformsResponse {
  success: boolean;
  message: string;
  data: {
    platforms: SupportedPlatform[];
    total: number;
  };
}

class ShopIntegrationsService {
  async getShopIntegrations(organizationId: string): Promise<ShopIntegrationsResponse> {
    const response = await api.get<ShopIntegrationsResponse>(`/shop-integrations/${organizationId}`);
    return response.data;
  }

  async createShopIntegration(organizationId: string, data: CreateShopIntegrationData): Promise<ShopIntegrationResponse> {
    const response = await api.post<ShopIntegrationResponse>(`/shop-integrations/${organizationId}`, data);
    return response.data;
  }

  async updateShopIntegration(id: string, data: UpdateShopIntegrationData): Promise<ShopIntegrationResponse> {
    const response = await api.put<ShopIntegrationResponse>(`/shop-integrations/${id}`, data);
    return response.data;
  }

  async deleteShopIntegration(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/shop-integrations/${id}`);
    return response.data;
  }

  async testShopIntegration(id: string): Promise<TestResponse> {
    const response = await api.post<TestResponse>(`/shop-integrations/${id}/test`);
    return response.data;
  }

  async getSupportedPlatforms(): Promise<PlatformsResponse> {
    const response = await api.get<PlatformsResponse>('/shop-integrations/platforms/list');
    return response.data;
  }

  getSupportedPlatformTypes(): PlatformType[] {
    return PLATFORM_ORDER;
  }

  getPlatformFields(platformType: PlatformType): PlatformFieldDef[] {
    return (PLATFORMS as Record<string, PlatformDef>)[platformType]?.fields ?? [];
  }

  getPlatformAuthHint(platformType: PlatformType): string {
    return (PLATFORMS as Record<string, PlatformDef>)[platformType]?.authHint ?? '';
  }

  /** Platform türüne göre boş konfigürasyon (varsayılanlar dahil) */
  getDefaultConfig(platformType: PlatformType): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of this.getPlatformFields(platformType)) out[f.name] = f.default ?? '';
    return out;
  }

  getPlatformIcon(platformType: PlatformType): string {
    return (PLATFORMS as Record<string, PlatformDef>)[platformType]?.icon ?? '🏪';
  }

  getPlatformDisplayName(platformType: PlatformType | string): string {
    if (platformType === 'opencart') return 'OpenCart';
    return (PLATFORMS as Record<string, PlatformDef>)[platformType]?.name ?? String(platformType);
  }

  getTestStatusColor(status?: string): string {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'timeout':
        return 'text-orange-600 bg-orange-50';
      case 'unauthorized':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  }

  getTestStatusLabel(status?: string): string {
    switch (status) {
      case 'success':
        return t('settings:shop.testOk', 'Verbindung erfolgreich');
      case 'failed':
        return t('settings:shop.testFailed', 'Verbindung fehlgeschlagen');
      case 'pending':
        return t('common:state.pending', 'Ausstehend');
      case 'timeout':
        return t('common:state.timeout', 'Zeitüberschreitung');
      case 'unauthorized':
        return t('common:state.unauthorized', 'Nicht autorisiert');
      default:
        return t('common:state.unknown', 'Unbekannt');
    }
  }

  /** Gizli alanları maskeler (frontend görüntüleme için) */
  getSafeConnectionConfig(config: Record<string, any>): Record<string, any> {
    const safeConfig: Record<string, any> = {};
    for (const [key, value] of Object.entries(config)) {
      if (/(token|secret|password|^api_key$)/i.test(key)) {
        safeConfig[key] = value && value !== '[HIDDEN]' ? '[HIDDEN]' : value;
      } else {
        safeConfig[key] = value;
      }
    }
    return safeConfig;
  }

  calculateSuccessRate(integration: ShopIntegration): number {
    if (integration.total_requests === 0) return 0;
    return Math.round((integration.successful_requests / integration.total_requests) * 100);
  }

  formatLastRequestTime(lastRequestAt?: string): string {
    if (!lastRequestAt) return t('errors:fix.neverUsed', 'Noch nie verwendet');

    const date = new Date(lastRequestAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('errors:fix.justNow', 'Gerade eben');
    if (diffInMinutes < 60) return t('errors:fix.minutesAgo', 'vor {{count}} Minuten', { count: diffInMinutes });
    if (diffInMinutes < 1440) return t('errors:fix.hoursAgo', 'vor {{count}} Stunden', { count: Math.floor(diffInMinutes / 60) });
    return t('errors:fix.daysAgo', 'vor {{count}} Tagen', { count: Math.floor(diffInMinutes / 1440) });
  }

  /** Zorunlu alan kontrolü — platform tanımından */
  validateConnectionConfig(platformType: PlatformType, config: Record<string, any>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    for (const f of this.getPlatformFields(platformType)) {
      if (f.required && !(config?.[f.name] && String(config[f.name]).trim())) {
        errors.push(t('errors:fix.required', '{{field}} ist erforderlich', { field: f.label }));
      }
    }
    if (platformType === 'shopify' && config?.shop_url && !String(config.shop_url).includes('.myshopify.com')) {
      errors.push(t('settings:shop.shopifyDomain', 'Shop URL muss die .myshopify.com-Domain enthalten'));
    }
    return { isValid: errors.length === 0, errors };
  }
}

export default new ShopIntegrationsService();
